/**
 * Payload primitives reused across resource schemas.
 *
 * Owns only field-level building blocks (ids, addresses, pagination, the signed
 * wallet-request envelope). Resource request/response shapes live in the sibling
 * `schemas/api/*.ts` files.
 */
import { z } from "zod";

/** Max rows any list endpoint may return. Bounds the worst-case query and the
 *  JSON payload; raising it needs an index review on the sorted column. */
export const MAX_PAGE_SIZE = 100;

/** Default page size — small enough for a first paint, large enough to fill a
 *  typical viewport without an immediate second request. */
export const DEFAULT_PAGE_SIZE = 30;

/** A v4 UUID primary key, as it appears in JSON (string, never a number). */
export const uuidSchema = z.uuid();

/**
 * Shared list query. `limit` is coerced because query strings are always
 * strings, and clamped in-schema so no handler has to re-check the bound.
 */
export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  /** Offset paging is fine at scaffold scale; keyset paging replaces it when a
   *  list outgrows a few thousand rows and deep offsets start scanning. */
  offset: z.coerce.number().int().min(0).default(0),
});

/** Resolved pagination arguments, defaults applied. */
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** ISO-8601 UTC instant, as serialized over JSON. Postgres `timestamptz`. */
export const isoInstantSchema = z.iso.datetime();

/**
 * A zero-padded SUI address: `0x` + exactly 64 hex digits.
 *
 * This is the form every endpoint that *acts on value* requires (claims, lease
 * creation, draw claims, the score endpoint). Anything looser lets a nonce — or
 * a URL path segment interpolated into an upstream request — be minted for an
 * arbitrary string.
 */
export const suiAddressSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/, "Invalid SUI address");

/**
 * The tolerant address form: `0x` + 1-64 hex digits.
 *
 * Deliberately NOT merged with `suiAddressSchema`. The public read-only
 * endpoints (`/api/rewards/info`, `/api/referrals/tree`) have always answered
 * the short, non-zero-padded address some clients send, and the retired
 * Cloudflare Workers they were ported from used exactly this pattern
 * (`SUI_ADDRESS_RE`). Tightening it here would 400 wallets that work today.
 */
export const suiAddressLooseSchema = z
  .string()
  .regex(/^0x[0-9a-fA-F]{1,64}$/, "Invalid SUI address");

/**
 * A wallet-signed message as the browser sends it: the raw bytes as a `number[]`
 * (what `Array.from(new TextEncoder().encode(text))` produces).
 *
 * Bounded at 1024 bytes and to byte range per element — the draw-claim route
 * already validated this by hand because an unbounded array is free work for an
 * unauthenticated caller to hand the signature verifier.
 */
export const signedMessageBytesSchema = z.array(z.number().int().min(0).max(255)).min(1).max(1024);

/**
 * The replay-protection envelope every value-moving endpoint carries.
 *
 * `nonce` + `nonceToken` come from `POST /api/auth/get-jwt-nonce`; `signature`
 * and `message` are the wallet's personal-message signature over text that must
 * itself bind the request parameters. Verifying the signature alone is not
 * enough — see `signed-intent.ts` in apps/api.
 */
export const signedRequestEnvelopeSchema = z.object({
  signature: z.string().min(10).max(2048),
  message: signedMessageBytesSchema,
  nonce: z.string().min(8).max(256),
  nonceToken: z.string().min(1),
});

/** Resolved signed-request envelope. */
export type SignedRequestEnvelope = z.infer<typeof signedRequestEnvelopeSchema>;

/**
 * A money amount as it crosses the wire.
 *
 * Kept as a JS `number` because that is what every existing client sends and
 * every ported route compared against. The authoritative value is always the
 * NUMERIC in Postgres; routes re-derive the payable figure in base units
 * (`token-amount.ts`) before writing anything.
 */
export const tokenAmountSchema = z.number().finite();

/**
 * An exact amount in base units (1 TRUTH = 1e8), as decimal digits.
 *
 * A string, unlike {@link tokenAmountSchema}: this is a CEILING the client
 * compares its own base units against, and a double cannot hold every 8-decimal
 * amount above ~9e7 TRUTH exactly. The client does `BigInt(value)`.
 */
export const baseUnitsSchema = z.string().regex(/^\d+$/, "Expected base units as digits");

/** Claim lifecycle, mirroring `enum_daily_rewards_claims_status` in the database. */
export const claimStatusSchema = z.enum(["pending", "processing", "completed", "quarantined"]);

/** Resolved claim status. */
export type ClaimStatus = z.infer<typeof claimStatusSchema>;

/**
 * Generic failure envelope: `{ error }` with an optional HTTP-ish detail.
 *
 * Used by the route groups whose Next.js originals returned a bare `{ error }`
 * rather than a zero-filled payload. Never carries driver text — see the error
 * handling note in each route file.
 */
export const errorResponseSchema = z.object({
  error: z.string(),
});

/** Resolved generic failure envelope. */
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
